import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Collapse, // Importação adicionada
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings"; // Ícone para o botão

interface PorcentagemComissaoProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

// O tipo de dado esperado da resposta GET
interface GetResponse {
  percentage: number;
}

const PorcentagemComissao: React.FC<PorcentagemComissaoProps> = ({
  token,
  role,
}) => {
  // NOVO ESTADO para controlar a visibilidade do formulário de edição
  const [showForm, setShowForm] = useState(false);

  const [comissao, setComissao] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalComissao, setOriginalComissao] = useState<number | null>(null);

  const API_URL =
    "https://api.anuncio360.com.br/admin/settings/referral-commission-percentage";

  // Função para buscar a porcentagem de comissão atual (GET)
  const fetchComissao = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setShowForm(false); // Garante que o formulário está fechado ao recarregar

    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Falha ao buscar a porcentagem atual.");
      }

      const data: GetResponse = await response.json();
      const percentageValue = data.percentage;

      // Atualiza os estados
      setComissao(percentageValue.toFixed(2)); // Formata para 2 casas decimais no input
      setOriginalComissao(percentageValue);
    } catch (err: any) {
      setError(err.message || "Erro desconhecido ao carregar os dados.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Efeito para carregar os dados quando o componente montar
  useEffect(() => {
    fetchComissao();
  }, [fetchComissao]);

  // Função para lidar com a atualização da porcentagem (PUT)
  const handleUpdateComissao = async () => {
    // 1. Validação simples do input (verificar se é um número válido)
    const numericValue = parseFloat(comissao);
    if (isNaN(numericValue) || numericValue < 0) {
      setError("Porcentagem inválida. Por favor, insira um número positivo.");
      return;
    }

    // Se o valor não mudou, evita a chamada PUT
    if (originalComissao !== null && numericValue === originalComissao) {
      setSuccess("A porcentagem já está com esse valor.");
      setShowForm(false); // Fecha o form se não houver alteração
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const body = {
        value: numericValue.toFixed(2), // Garante o formato "XX.XX"
      };

      const response = await fetch(API_URL, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Erro desconhecido" }));
        throw new Error(
          errorData.message ||
            `Falha na atualização. Status: ${response.status}`
        );
      }

      // Se o PUT for bem-sucedido, atualiza o valor original, exibe sucesso e fecha o form
      setOriginalComissao(numericValue);
      setSuccess("Porcentagem de comissão atualizada com sucesso!");
      setShowForm(false); // Fecha o form após sucesso
    } catch (err: any) {
      setError(err.message || "Erro desconhecido ao tentar atualizar.");
    } finally {
      setIsLoading(false);
    }
  };

  // Restrição de acesso: Apenas o MASTER pode alterar a porcentagem
  const isEditable = role === "MASTER";

  // --- Renderização do Componente ---

  if (isLoading && originalComissao === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 400, mx: "auto" }}>
      <Typography variant="h6" gutterBottom>
        Porcentagem da Comissão
      </Typography>

      {/* Exibe o valor atual da comissão SEMPRE, mesmo quando o formulário está fechado */}
      <Typography variant="body1" sx={{ mb: 2 }}>
        Valor Atual:{" "}
        {originalComissao !== null
          ? `${originalComissao.toFixed(2)}%`
          : "Não disponível"}
      </Typography>

      {/* Exibe restrição de role */}
      {!isEditable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sua role **({role})** não permite a alteração da porcentagem.
        </Alert>
      )}

      {/* Exibe mensagens de status */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* BOTÃO PARA MOSTRAR/OCULTAR O FORMULÁRIO DE EDIÇÃO */}
      {isEditable && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color={showForm ? "secondary" : "primary"}
            startIcon={<SettingsIcon />}
            onClick={() => {
              setShowForm(!showForm);
              // Limpa mensagens de erro/sucesso ao abrir/fechar o formulário
              setError(null);
              setSuccess(null);
            }}
            disabled={isLoading}
            fullWidth
          >
            {showForm ? "Cancelar Edição" : "Alterar Porcentagem"}
          </Button>
        </Box>
      )}

      {/* COLLAPSE que envolve o campo de edição e o botão de salvar */}
      <Collapse in={showForm}>
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ddd",
            borderRadius: "4px",
            mt: 1, // Pequeno espaçamento após a borda
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Novo Valor
          </Typography>
          <TextField
            label="Nova Porcentagem de Comissão (%)"
            variant="outlined"
            fullWidth
            type="number"
            value={comissao}
            onChange={(e) => setComissao(e.target.value)}
            disabled={isLoading}
            InputProps={{
              endAdornment: <Typography color="textSecondary">%</Typography>,
            }}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateComissao}
            disabled={isLoading || !comissao}
            fullWidth
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Salvar Alteração"
            )}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PorcentagemComissao;
